import React, { useEffect, useContext } from 'react'
import { Grid, TextField, Box, Typography, Button, IconButton, Tooltip } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded'
import DeleteIcon from '@mui/icons-material/Delete'
import { TreeItem, TreeView } from '@mui/x-tree-view'
import CollapseIcon from '@mui/icons-material/ExpandMore'
import ExpandIcon from '@mui/icons-material/ChevronRight'
import { useForm, Controller } from 'react-hook-form'
import { emailPattern } from './CompanyDetails'
import { SwalContext } from 'genesis-suite/components'
import { v1 as uuid } from 'uuid'

const initialValue = {
    name: '',
    friendlyName: '',
    adminEmailAddress: '',
    id: '',
}
const DomainDetails = ({
    selectedDomain,
    setSelectedDomain,
    selectedCompany,
    onSave,
    onDelete,
    domainState,
    setDomainState,
}) => {
    const { confirm } = useContext(SwalContext)
    const {
        isAddingDomain = false,
        isAddingSubdomain = false,
        selectedNodeId = null,
        parentDomain = null,
        expandedNodes = [],
    } = domainState || {}

    const domains = selectedCompany?.domains || []
    const {
        control,
        handleSubmit,
        reset,
        formState: { isDirty, isValid, errors },
        setValue,
        getValues,
        watch,
        trigger,
    } = useForm({
        mode: 'onChange',
        defaultValues: initialValue,
    })
    const nameValue = watch('name')
    const email = watch('adminEmailAddress')
    const friendlyNameValue = watch('friendlyName')

    const isSameAsOriginalData = () => {
        if (!selectedDomain) return false
        const currentValues = getValues()
        return (
            currentValues.name === selectedDomain.name &&
            currentValues.friendlyName === selectedDomain.friendlyName &&
            currentValues.adminEmailAddress === selectedDomain.adminEmailAddress
        )
    }
    const isFormChangedAndValid = isDirty && isValid && !isSameAsOriginalData()

    useEffect(() => {
        if (selectedDomain) {
            setValue('name', selectedDomain.name)
            setValue('friendlyName', selectedDomain.friendlyName)
            setValue('adminEmailAddress', selectedDomain.adminEmailAddress)
            setValue('id', selectedDomain.id)
        }
    }, [selectedDomain, setValue])

    const validateEmailDomain = () => {
        const email = getValues('adminEmailAddress')
        if (!email || !emailPattern.test(email)) return true
        const emailDomain = email.split('@')[1]?.toLowerCase()
        const name = isAddingSubdomain ? parentDomain?.name?.toLowerCase() : nameValue
        return emailDomain === name || 'Email domain does not match company URL domain'
    }

    useEffect(() => {
        if (email && nameValue && isDirty) {
            trigger('adminEmailAddress')
        }
    }, [email, nameValue, isDirty, trigger])

    const findParentDomain = (domains, subdomainId) => {
        for (const domain of domains) {
            if (domain.subDomains) {
                const subDomain = domain.subDomains.find(sub => sub.id === subdomainId)
                if (subDomain) {
                    return domain
                }
                const parentDomain = findParentDomain(domain.subDomains, subdomainId)
                if (parentDomain) {
                    return parentDomain
                }
            }
        }
        return null
    }
    const handleNodeSelect = (event, nodeId) => {
        setDomainState(prevState => ({ ...prevState, selectedNodeId: nodeId }))

        const domain = domains
            .flatMap(domain => [domain, ...(domain.subDomains || [])])
            .find(domain => domain.id === nodeId)

        if (domain) {
            setSelectedDomain(domain)

            if (domain.subDomains && domain.subDomains.length > 0) {
                // It's a parent domain
                setDomainState(prevState => ({
                    ...prevState,
                    isAddingSubdomain: false,
                    parentDomain: domain,
                }))
            } else if (domain.parentId) {
                // It's a child domain (subdomain)
                const parent = domains.find(d => d.id === domain.parentId)
                setDomainState(prevState => ({
                    ...prevState,
                    isAddingSubdomain: true,
                    parentDomain: parent,
                }))
            } else if (domain?.isSubdomain) {
                const parentDomainInfo = findParentDomain(domains, domain?.id)
                setDomainState(prevState => ({
                    ...prevState,
                    isAddingSubdomain: true,
                    parentDomain: parentDomainInfo,
                }))
            } else {
                // It's a root domain
                setDomainState(prevState => ({
                    ...prevState,
                    parentDomain: domain,
                    isAddingSubdomain: false,
                }))
            }
        }
    }
    const handleAddSubDomain = () => {
        resetFields()
        setDomainState(prevState => ({
            ...prevState,
            isAddingSubdomain: true,
        }))
    }

    const handleAddDomain = () => {
        resetFields()
        setDomainState(prevState => ({
            ...prevState,
            isAddingDomain: true,
            isAddingSubdomain: false,
        }))
    }

    const resetFields = () => {
        reset(initialValue)
    }

    const onSubmit = data => {
        const domainData = {
            name: data.name,
            friendlyName: data.friendlyName,
            adminEmailAddress: data.adminEmailAddress,
            isSubdomain: isAddingSubdomain,
            parentDomain: parentDomain,
            id: data.id || uuid(),
        }
        const updatedDomain = onSave(domainData)
        if (updatedDomain?.isError) return

        // Find the updated domain in the subdomains or domain list
        const domain =
            updatedDomain
                ?.map(domain => [domain, ...(domain.subDomains || [])])
                .flat()
                .find(d => d.id === domainData?.id) || updatedDomain

        if (domain) setSelectedDomain(domain)

        // Update the parent domain if it's a subdomain, otherwise update as a root domain
        setDomainState(prevState => ({
            ...prevState,
            parentDomain:
                isAddingSubdomain && parentDomain
                    ? { ...parentDomain, subDomains: [...(parentDomain.subDomains || []), updatedDomain] }
                    : domain ?? updatedDomain,
            selectedNodeId: data.id,
        }))
    }

    const renderTreeItems = domain => {
        return (
            <TreeItem
                sx={{ marginTop: '10px' }}
                key={domain.id}
                nodeId={domain.id}
                label={
                    <span>
                        <strong>{domain.name}</strong>
                        <br />
                        <small>{`( ${domain.friendlyName} )`}</small>
                    </span>
                }
            >
                {domain.subDomains?.map(subDomain => renderTreeItems(subDomain))}
            </TreeItem>
        )
    }
    const handleDeleteDomain = async () => {
        const response = await confirm(
            'Deleting the selected domain will delete all its sub domains and will leave all the apps & users of this company orphaned. This operation cannot be undone. Do you want to proceed?',
            {
                type: 'warning',
                confirmButtonProps: { text: 'Delete' },
            }
        )
        if (response.dismiss) return

        const currentDomains = [...domains]

        if (selectedDomain) {
            const updatedDomains = currentDomains
                .map(domain => {
                    if (domain.id === selectedDomain.id || selectedDomain.id === '' || selectedDomain.id === null) {
                        return null
                    }
                    if (domain.subDomains) {
                        domain.subDomains = domain.subDomains.filter(
                            subDomain =>
                                subDomain.id !== selectedDomain.id && subDomain.id !== '' && subDomain.id !== null
                        )
                    }
                    return domain
                })
                .filter(Boolean)
            onDelete({
                ...selectedCompany,
                domains: updatedDomains,
            })

            setSelectedDomain(null)
            resetFields()
        }
    }
    const handleToggleExpand = (event, nodeIds) => {
        setDomainState(prevState => ({
            ...prevState,
            expandedNodes: nodeIds,
        }))
    }

    const isDomainSelected = domains.some(domain => domain.id === selectedNodeId)

    return (
        <Grid container spacing={2} style={{ height: '100%' }}>
            <Grid item xs={6} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    padding="10px"
                    bgcolor="#f0f0f0"
                    borderRadius="8px"
                    marginBottom="15px"
                >
                    <Typography variant="h6">Domains</Typography>
                </Box>

                <Box display="flex" mb={2}>
                    <Tooltip title="Add Domain">
                        <IconButton color="inherit" onClick={handleAddDomain}>
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Add Sub Domain">
                        <IconButton
                            color="inherit"
                            disabled={!isDomainSelected || isAddingSubdomain}
                            onClick={handleAddSubDomain}
                        >
                            <AddCircleRoundedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Selected Domain">
                        <IconButton
                            color="inherit"
                            disabled={!selectedNodeId || selectedDomain === null || selectedDomain?.isRoot}
                            onClick={handleDeleteDomain}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Box sx={{ flexGrow: 1, maxHeight: '300px', overflowY: 'auto' }}>
                    {domains?.length === 0 ? (
                        <Typography align="center" style={{ marginTop: '20%', color: '#7b7b7b' }}>
                            Create a new domain by clicking the <AddIcon /> icon.
                        </Typography>
                    ) : (
                        <TreeView
                            defaultCollapseIcon={<CollapseIcon />}
                            defaultExpandIcon={<ExpandIcon />}
                            selected={selectedNodeId}
                            expanded={expandedNodes}
                            onNodeSelect={handleNodeSelect}
                            onNodeToggle={handleToggleExpand}
                        >
                            {domains.map(domain => renderTreeItems(domain))}
                        </TreeView>
                    )}
                </Box>
            </Grid>

            <Grid item xs={5} style={{ marginLeft: '30px' }}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    padding="10px"
                    bgcolor="#f0f0f0"
                    borderRadius="8px"
                    marginBottom="15px"
                >
                    <Typography variant="h6">Domain Details</Typography>
                </Box>

                {selectedDomain || isAddingDomain ? (
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                {isAddingSubdomain ? (
                                    <Box display="flex" alignItems="center" style={{ marginTop: '10px' }}>
                                        <Controller
                                            name="name"
                                            control={control}
                                            rules={{ required: 'Subdomain Name is required' }}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    label="Name"
                                                    value={nameValue?.replace(`.${parentDomain?.name}`, '')}
                                                    onChange={e =>
                                                        setValue('name', e.target.value + '.' + parentDomain?.name, {
                                                            shouldDirty: true,
                                                        })
                                                    }
                                                    fullWidth
                                                    style={{ marginRight: '10px' }}
                                                    error={!!errors.name}
                                                    helperText={errors.name ? errors.name.message : ''}
                                                    required
                                                />
                                            )}
                                        />
                                        {parentDomain && <Typography variant="h6">.{parentDomain.name}</Typography>}
                                    </Box>
                                ) : (
                                    <Controller
                                        name="name"
                                        control={control}
                                        rules={{ required: 'Domain Name is required' }}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                label="Name"
                                                fullWidth
                                                style={{ marginTop: '10px' }}
                                                error={!!errors.name}
                                                helperText={errors.name ? errors.name.message : ''}
                                                required
                                            />
                                        )}
                                    />
                                )}

                                <Controller
                                    name="friendlyName"
                                    control={control}
                                    rules={{ required: 'Friendly Name is required' }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label="Friendly Name"
                                            fullWidth
                                            style={{ marginTop: '10px' }}
                                            error={!!errors.friendlyName}
                                            helperText={errors.friendlyName ? errors.friendlyName?.message : ''}
                                            required
                                        />
                                    )}
                                />

                                <Controller
                                    name="adminEmailAddress"
                                    control={control}
                                    rules={{
                                        required: 'Administrator Email is required',
                                        pattern: {
                                            value: emailPattern,
                                            message: 'Enter a valid email address',
                                        },
                                        validate: validateEmailDomain,
                                    }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label="Administrator Email"
                                            fullWidth
                                            style={{ marginTop: '10px' }}
                                            error={!!errors.adminEmailAddress}
                                            helperText={errors.adminEmailAddress?.message}
                                            required
                                        />
                                    )}
                                />

                                <Box display="flex" justifyContent="flex-end" marginTop="20px">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        disabled={!isFormChangedAndValid || !!Object.keys(errors).length}
                                    >
                                        OK
                                    </Button>
                                    <Button
                                        sx={{ marginLeft: '10px' }}
                                        variant="contained"
                                        color="secondary"
                                        onClick={() => {
                                            setSelectedDomain(null)
                                            setDomainState(null)
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                ) : (
                    <Typography align="center" style={{ marginTop: '20%', color: '#7b7b7b' }}>
                        Select a domain from the list on the left or add a new domain by clicking the <AddIcon /> icon.
                    </Typography>
                )}
            </Grid>
        </Grid>
    )
}

export default DomainDetails
